"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui/button";
import { ButtonGroup, ButtonGroupText } from "@/components/ui/button-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { FileUIPart, UIMessage } from "ai";
import { ChevronLeftIcon, ChevronRightIcon, PaperclipIcon, XIcon } from "lucide-react";
import Image from "next/image";
import type { ComponentProps, HTMLAttributes, ReactElement } from "react";
import { Children, createContext, isValidElement, memo, useContext, useEffect, useMemo, useState } from "react";
import { Streamdown } from "streamdown";
export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
};
export const Message = t0 => {
  const $ = _c(10);
  let className;
  let from;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      from,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = from;
    $[3] = props;
  } else {
    className = $[1];
    from = $[2];
    props = $[3];
  }
  const t1 = from === "user" ? "is-user ml-auto justify-end" : "is-assistant";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("group flex w-full max-w-[95%] flex-col gap-2", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== t2) {
    t3 = <div className={t2} {...props} />;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export type MessageContentProps = HTMLAttributes<HTMLDivElement>;
export const MessageContent = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("is-user:dark flex w-fit max-w-full min-w-0 flex-col gap-2 overflow-hidden text-sm", "group-[.is-user]:bg-secondary group-[.is-user]:text-foreground group-[.is-user]:ml-auto group-[.is-user]:rounded-lg group-[.is-user]:px-4 group-[.is-user]:py-3", "group-[.is-assistant]:text-foreground", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export type MessageActionsProps = ComponentProps<"div">;
export const MessageActions = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex items-center gap-1", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export type MessageActionProps = ComponentProps<typeof Button> & {
  tooltip?: string;
  label?: string;
};
export const MessageAction = t0 => {
  const $ = _c(22);
  let children;
  let label;
  let props;
  let t1;
  let t2;
  let tooltip;
  if ($[0] !== t0) {
    ({
      tooltip,
      children,
      label,
      variant: t1,
      size: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = label;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = tooltip;
  } else {
    children = $[1];
    label = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    tooltip = $[6];
  }
  const variant = t1 === undefined ? "ghost" : t1;
  const size = t2 === undefined ? "icon-sm" : t2;
  const t3 = label ?? tooltip;
  let t4;
  if ($[7] !== t3) {
    t4 = <span className="sr-only">{t3}</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== children || $[10] !== props || $[11] !== size || $[12] !== t4 || $[13] !== variant) {
    t5 = <Button size={size} type="button" variant={variant} {...props}>{children}{t4}</Button>;
    $[9] = children;
    $[10] = props;
    $[11] = size;
    $[12] = t4;
    $[13] = variant;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const button = t5;
  if (tooltip) {
    let t6;
    if ($[15] !== button) {
      t6 = <TooltipTrigger asChild={true}>{button}</TooltipTrigger>;
      $[15] = button;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== tooltip) {
      t7 = <TooltipContent><p>{tooltip}</p></TooltipContent>;
      $[17] = tooltip;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== t6 || $[20] !== t7) {
      t8 = <TooltipProvider><Tooltip>{t6}{t7}</Tooltip></TooltipProvider>;
      $[19] = t6;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  return button;
};
interface MessageBranchContextType {
  currentBranch: number;
  totalBranches: number;
  goToPrevious: () => void;
  goToNext: () => void;
  branches: ReactElement[];
  setBranches: (branches: ReactElement[]) => void;
}
const MessageBranchContext = createContext<MessageBranchContextType | null>(null);
const useMessageBranch = () => {
  const context = useContext(MessageBranchContext);
  if (!context) {
    throw new Error("MessageBranch components must be used within MessageBranch");
  }
  return context;
};
export type MessageBranchProps = HTMLAttributes<HTMLDivElement> & {
  defaultBranch?: number;
  onBranchChange?: (branchIndex: number) => void;
};
export const MessageBranch = t0 => {
  const $ = _c(29);
  let className;
  let onBranchChange;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      defaultBranch: t1,
      onBranchChange,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onBranchChange;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    onBranchChange = $[2];
    props = $[3];
    t1 = $[4];
  }
  const defaultBranch = t1 === undefined ? 0 : t1;
  const [currentBranch, setCurrentBranch] = useState(defaultBranch);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [branches, setBranches] = useState(t2);
  let t3;
  if ($[6] !== onBranchChange) {
    t3 = newBranch => {
      setCurrentBranch(newBranch);
      onBranchChange?.(newBranch);
    };
    $[6] = onBranchChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleBranchChange = t3;
  let t4;
  if ($[8] !== branches.length || $[9] !== currentBranch || $[10] !== handleBranchChange) {
    t4 = () => {
      const newBranch_0 = currentBranch > 0 ? currentBranch - 1 : branches.length - 1;
      handleBranchChange(newBranch_0);
    };
    $[8] = branches.length;
    $[9] = currentBranch;
    $[10] = handleBranchChange;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const goToPrevious = t4;
  let t5;
  if ($[12] !== branches.length || $[13] !== currentBranch || $[14] !== handleBranchChange) {
    t5 = () => {
      const newBranch_1 = currentBranch < branches.length - 1 ? currentBranch + 1 : 0;
      handleBranchChange(newBranch_1);
    };
    $[12] = branches.length;
    $[13] = currentBranch;
    $[14] = handleBranchChange;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const goToNext = t5;
  let t6;
  if ($[16] !== branches || $[17] !== currentBranch || $[18] !== goToNext || $[19] !== goToPrevious) {
    t6 = {
      currentBranch,
      totalBranches: branches.length,
      goToPrevious,
      goToNext,
      branches,
      setBranches
    };
    $[16] = branches;
    $[17] = currentBranch;
    $[18] = goToNext;
    $[19] = goToPrevious;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const contextValue = t6;
  let t7;
  if ($[21] !== className) {
    t7 = cn("grid w-full gap-2 [&>div]:pb-0", className);
    $[21] = className;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== props || $[24] !== t7) {
    t8 = <div className={t7} {...props} />;
    $[23] = props;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== contextValue || $[27] !== t8) {
    t9 = <MessageBranchContext.Provider value={contextValue}>{t8}</MessageBranchContext.Provider>;
    $[26] = contextValue;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
export type MessageBranchContentProps = HTMLAttributes<HTMLDivElement>;
export const MessageBranchContent = t0 => {
  const $ = _c(20);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const {
    currentBranch,
    setBranches,
    branches
  } = useMessageBranch();
  let t1;
  if ($[3] !== children) {
    t1 = Children.toArray(children).filter(isValidElement);
    $[3] = children;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const childrenArray = t1;
  let t2;
  if ($[5] !== branches.length || $[6] !== childrenArray || $[7] !== setBranches) {
    t2 = () => {
      if (branches.length !== childrenArray.length) {
        setBranches(childrenArray);
      }
    };
    $[5] = branches.length;
    $[6] = childrenArray;
    $[7] = setBranches;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== branches || $[10] !== childrenArray || $[11] !== setBranches) {
    t3 = [childrenArray, branches, setBranches];
    $[9] = branches;
    $[10] = childrenArray;
    $[11] = setBranches;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  useEffect(t2, t3);
  let t4;
  if ($[13] !== childrenArray || $[14] !== currentBranch || $[15] !== props) {
    let t5;
    if ($[17] !== currentBranch || $[18] !== props) {
      t5 = (branch, index) => <div className={cn("grid gap-2 overflow-hidden [&>div]:pb-0", index === currentBranch ? "block" : "hidden")} key={branch.key} {...props}>{branch}</div>;
      $[17] = currentBranch;
      $[18] = props;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    t4 = childrenArray.map(t5);
    $[13] = childrenArray;
    $[14] = currentBranch;
    $[15] = props;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
export type MessageBranchSelectorProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
};
export const MessageBranchSelector = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    totalBranches
  } = useMessageBranch();
  if (totalBranches <= 1) {
    return null;
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("[&>*:not(:first-child)]:rounded-l-md [&>*:not(:last-child)]:rounded-r-md", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ButtonGroup className={t1} orientation="horizontal" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type MessageBranchPreviousProps = ComponentProps<typeof Button>;
export const MessageBranchPrevious = t0 => {
  const $ = _c(10);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const {
    goToPrevious,
    totalBranches
  } = useMessageBranch();
  const t1 = totalBranches <= 1;
  let t2;
  if ($[3] !== children) {
    t2 = children ?? <ChevronLeftIcon size={14} />;
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== goToPrevious || $[6] !== props || $[7] !== t1 || $[8] !== t2) {
    t3 = <Button aria-label="Previous branch" disabled={t1} onClick={goToPrevious} size="icon-sm" type="button" variant="ghost" {...props}>{t2}</Button>;
    $[5] = goToPrevious;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export type MessageBranchNextProps = ComponentProps<typeof Button>;
export const MessageBranchNext = t0 => {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    goToNext,
    totalBranches
  } = useMessageBranch();
  const t1 = totalBranches <= 1;
  let t2;
  if ($[4] !== children) {
    t2 = children ?? <ChevronRightIcon size={14} />;
    $[4] = children;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== className || $[7] !== goToNext || $[8] !== props || $[9] !== t1 || $[10] !== t2) {
    t3 = <Button aria-label="Next branch" className={className} disabled={t1} onClick={goToNext} size="icon-sm" type="button" variant="ghost" {...props}>{t2}</Button>;
    $[6] = className;
    $[7] = goToNext;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export type MessageBranchPageProps = HTMLAttributes<HTMLSpanElement>;
export const MessageBranchPage = t0 => {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    currentBranch,
    totalBranches
  } = useMessageBranch();
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-muted-foreground border-none bg-transparent shadow-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = currentBranch + 1;
  let t3;
  if ($[5] !== props || $[6] !== t1 || $[7] !== t2 || $[8] !== totalBranches) {
    t3 = <ButtonGroupText className={t1} {...props}>{t2} of {totalBranches}</ButtonGroupText>;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = totalBranches;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export type MessageResponseProps = ComponentProps<typeof Streamdown>;
export const MessageResponse = memo(t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Streamdown className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}, (prevProps, nextProps) => prevProps.children === nextProps.children);
MessageResponse.displayName = "MessageResponse";
export type MessageAttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: FileUIPart;
  className?: string;
  onRemove?: () => void;
};
export function MessageAttachment(t0) {
  const $ = _c(17);
  let className;
  let data;
  let onRemove;
  let props;
  if ($[0] !== t0) {
    ({
      data,
      className,
      onRemove,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = data;
    $[3] = onRemove;
    $[4] = props;
  } else {
    className = $[1];
    data = $[2];
    onRemove = $[3];
    props = $[4];
  }
  const filename = data.filename ?? "";
  const mediaType = data.mediaType.startsWith("image/") && data.url ? "image" : "file";
  const isImage = mediaType === "image";
  const attachmentLabel = filename || (isImage ? "Image" : "Attachment");
  let t1;
  if ($[5] !== className) {
    t1 = cn("group relative size-24 overflow-hidden rounded-lg", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== attachmentLabel || $[8] !== data.url || $[9] !== filename || $[10] !== isImage || $[11] !== onRemove) {
    t2 = isImage ? <><Image alt={filename || "attachment"} className="size-full object-cover" height={100} src={data.url} width={100} />{onRemove && <Button aria-label="Remove attachment" className="bg-background/80 hover:bg-background absolute top-2 right-2 size-6 rounded-full p-0 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 [&>svg]:size-3" onClick={e => {
        e.stopPropagation();
        onRemove();
      }} type="button" variant="ghost"><XIcon /><span className="sr-only">Remove</span></Button>}</> : <><Tooltip><TooltipTrigger asChild={true}><div className="bg-muted text-muted-foreground flex size-full shrink-0 items-center justify-center rounded-lg"><PaperclipIcon className="size-4" /></div></TooltipTrigger><TooltipContent><p>{attachmentLabel}</p></TooltipContent></Tooltip>{onRemove && <Button aria-label="Remove attachment" className="hover:bg-accent size-6 shrink-0 rounded-full p-0 opacity-0 transition-opacity group-hover:opacity-100 [&>svg]:size-3" onClick={e_0 => {
        e_0.stopPropagation();
        onRemove();
      }} type="button" variant="ghost"><XIcon /><span className="sr-only">Remove</span></Button>}</>;
    $[7] = attachmentLabel;
    $[8] = data.url;
    $[9] = filename;
    $[10] = isImage;
    $[11] = onRemove;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== props || $[14] !== t1 || $[15] !== t2) {
    t3 = <div className={t1} {...props}>{t2}</div>;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}
export type MessageAttachmentsProps = ComponentProps<"div">;
export function MessageAttachments(t0) {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  if (!children) {
    return null;
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("ml-auto flex w-fit flex-wrap items-start gap-2", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export type MessageToolbarProps = ComponentProps<"div">;
export const MessageToolbar = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("mt-4 flex w-full items-center justify-between gap-4", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};